import { z } from 'zod';

import { INSPECTION_CODE_PATTERN } from '#/modules/imsMgmt/settings/inspectionKind/helper/constants';

const DATA_TYPES = ['DECIMAL', 'BOOLEAN', 'SINGLE_SELECT', 'JUDGMENT', 'TEXT'];
const DECIMAL_OPERATORS = [
  'BETWEEN',
  'GREATER_THAN',
  'GREATER_THAN_OR_EQUAL',
  'LESS_THAN',
  'LESS_THAN_OR_EQUAL',
  'EQUALS',
];

function issue(ctx, path, message) {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path,
    message,
  });
}

function isDecimalText(value, scale) {
  const text = String(value ?? '').trim();
  if (!/^-?\d+(\.\d+)?$/.test(text)) {
    return false;
  }
  const fraction = text.includes('.') ? text.split('.')[1] : '';
  const digits = text.replace('-', '').replace('.', '');
  return fraction.length <= scale && digits.length > 0 && digits.length <= 38;
}

function inspectionCodeField(emptyMessage) {
  return z.string().trim().min(1, emptyMessage).refine(
    (value) => INSPECTION_CODE_PATTERN.test(String(value).toUpperCase()),
    '代碼須為大寫英數或底線，並以英文字母開頭',
  );
}

const optionSchema = z.object({
  value: z.string(),
  label: z.string(),
});

const itemSchema = z.object({
  locked: z.boolean(),
  itemCode: inspectionCodeField('條件代碼不能為空'),
  itemName: z.string().trim().min(1, '條件名稱不能為空'),
  dataType: z.enum(DATA_TYPES, { message: '請選擇資料型別' }),
  unit: z.string().optional().or(z.literal('')),
  required: z.boolean(),
  remark: z.string().optional().or(z.literal('')),
  operator: z.string(),
  scale: z.string().optional().or(z.literal('')),
  min: z.string().optional().or(z.literal('')),
  max: z.string().optional().or(z.literal('')),
  minInclusive: z.boolean(),
  maxInclusive: z.boolean(),
  expectedValue: z.string().optional().or(z.literal('')),
  options: z.array(optionSchema),
  passingValues: z.array(z.string()),
});

function validateDecimal(item, index, ctx) {
  const base = ['items', index];
  if (!DECIMAL_OPERATORS.includes(item.operator)) {
    issue(ctx, [...base, 'operator'], '請選擇數值比較方式');
    return;
  }
  if (!/^\d+$/.test(String(item.scale ?? '')) || Number(item.scale) > 12) {
    issue(ctx, [...base, 'scale'], '小數位數須為 0 到 12 的整數');
    return;
  }
  const scale = Number(item.scale);
  if (String(item.unit ?? '').trim() && item.dataType !== 'DECIMAL') {
    issue(ctx, [...base, 'unit'], '只有數值條件可以填單位');
  }
  if (item.operator === 'BETWEEN') {
    if (!isDecimalText(item.min, scale)) {
      issue(ctx, [...base, 'min'], '請填寫符合小數位數的下限');
    }
    if (!isDecimalText(item.max, scale)) {
      issue(ctx, [...base, 'max'], '請填寫符合小數位數的上限');
    }
    if (isDecimalText(item.min, scale) && isDecimalText(item.max, scale)
      && Number(item.min) > Number(item.max)) {
      issue(ctx, [...base, 'max'], '上限不可小於下限');
    }
    return;
  }
  if (!isDecimalText(item.expectedValue, scale)) {
    issue(ctx, [...base, 'expectedValue'], '請填寫符合小數位數的期望值');
  }
}

function validateSingleSelect(item, index, ctx) {
  const base = ['items', index];
  const filled = (item.options ?? []).filter((option) => (
    String(option?.value ?? '').trim() || String(option?.label ?? '').trim()
  ));
  if (!filled.length) {
    issue(ctx, [...base, 'options'], '請至少新增一個選項');
    return;
  }
  const seen = new Set();
  filled.forEach((option, optionIndex) => {
    const value = String(option.value ?? '').trim();
    const label = String(option.label ?? '').trim();
    if (!value || !label) {
      issue(ctx, [...base, 'options', optionIndex, 'value'], '選項需要代碼與名稱');
      return;
    }
    if (seen.has(value)) {
      issue(ctx, [...base, 'options', optionIndex, 'value'], '選項代碼不可重複');
      return;
    }
    seen.add(value);
  });
  const passing = item.passingValues ?? [];
  if (!passing.length || passing.some((value) => !seen.has(value))) {
    issue(ctx, [...base, 'passingValues'], '請至少勾選一個合格選項');
  }
}

function validateItem(item, index, ctx) {
  const base = ['items', index];
  if (item.dataType !== 'DECIMAL' && String(item.unit ?? '').trim()) {
    issue(ctx, [...base, 'unit'], '只有數值條件可以填單位');
  }
  if (item.dataType === 'DECIMAL') {
    validateDecimal(item, index, ctx);
    return;
  }
  if (item.dataType === 'BOOLEAN' && item.expectedValue !== 'true' && item.expectedValue !== 'false') {
    issue(ctx, [...base, 'expectedValue'], '請選擇期望值');
    return;
  }
  if (item.dataType === 'SINGLE_SELECT') {
    validateSingleSelect(item, index, ctx);
    return;
  }
  if (item.dataType === 'TEXT' && item.operator === 'EXACT_EQUALS'
    && !String(item.expectedValue ?? '').trim()) {
    issue(ctx, [...base, 'expectedValue'], '請填寫期望文字');
  }
}

export const inspectionKindFormSchema = z.object({
  code: inspectionCodeField('種類代碼不能為空'),
  name: z.string().trim().min(1, '名稱不能為空'),
  status: z.enum(['ACTIVE', 'INACTIVE'], { message: '請選擇狀態' }),
  items: z.array(itemSchema).min(1, '請至少新增一項條件'),
}).superRefine((values, ctx) => {
  const seen = new Set();
  values.items.forEach((item, index) => {
    const code = String(item.itemCode ?? '').trim().toUpperCase();
    if (code && seen.has(code)) {
      issue(ctx, ['items', index, 'itemCode'], '條件代碼不可重複');
    } else if (code) {
      seen.add(code);
    }
    validateItem(item, index, ctx);
  });
});
